/**
 * One garment in the grid.
 *
 * PRD-wardrobe.md §4, stage 2: bulk intake catalogues the wardrobe without producing
 * per-item photos, so **most tiles will have no image and that is the normal case, not
 * a broken one.** A tile without a photo shows a colour swatch carrying the garment's
 * actual colours, with the name, pattern and fabric written on it. It is legible,
 * it is honest, and it never renders an empty grey box or a broken-image icon.
 *
 * The swatch is decoration; the text is the data. A colour name we don't recognise
 * still reads fine because the word is right there.
 */
import { useEffect, useState } from 'react';
import type { StyleVerdict, WardrobeItem } from '../../shared/types';
import { Chip, cx, photoSrc, swatchStyle, type ChipTone } from './ui';

const VERDICT_META: Record<StyleVerdict, { label: string; tone: ChipTone }> = {
  keep: { label: 'Keep', tone: 'ok' },
  maybe: { label: 'Maybe', tone: 'near' },
  retire: { label: 'Retire', tone: 'over' },
};

export default function ItemTile({
  item,
  categoryLabel,
  onEdit,
}: {
  item: WardrobeItem;
  categoryLabel: string;
  onEdit: (item: WardrobeItem) => void;
}) {
  const verdict = item.style_verdict ? VERDICT_META[item.style_verdict] : null;
  const colorText = item.colors.length ? item.colors.join(' / ') : 'colour not recorded';

  /**
   * `has_image` says a KEY is recorded, not that the object is fetchable. The two can
   * disagree — a row pointing at an object that is not in this environment's bucket, which
   * is the normal state of local dev against a database seeded from production. Without
   * this the tile renders the browser's broken-image glyph, the exact thing the swatch
   * design exists to avoid, and the exact thing Brian reported for the brown belt.
   */
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [item.image_r2_key]);
  const showPhoto = item.has_image && !broken;

  return (
    <li>
      <button
        type="button"
        onClick={() => onEdit(item)}
        className={cx(
          'group flex w-full flex-col overflow-hidden rounded-card border border-border bg-surface text-left',
          'transition-shadow hover:shadow-[0_2px_10px_rgba(43,39,35,0.08)]',
          'focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
          'focus-visible:ring-offset-canvas focus-visible:outline-none'
        )}
      >
        {/* The visual. Photo when we have one, swatch when we don't. */}
        <span className="relative block aspect-square w-full">
          {showPhoto ? (
            <img
              src={photoSrc(item)}
              alt={item.name}
              loading="lazy"
              onError={() => setBroken(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <span
              className="flex h-full w-full items-end p-2.5"
              style={swatchStyle(item.colors)}
              aria-hidden="true"
            >
              {/* Colour words on the swatch itself, in a chip so they stay legible on
                  cream and on charcoal alike without knowing which we drew. */}
              <span className="rounded-md bg-surface/85 px-1.5 py-0.5 text-[11px] leading-tight font-medium text-ink">
                {colorText}
              </span>
            </span>
          )}

          {item.condition === 'retire' && (
            <span className="absolute top-2 right-2">
              <Chip tone="over">Retiring</Chip>
            </span>
          )}
        </span>

        <span className="flex flex-1 flex-col gap-1 p-3">
          <span className="font-display text-sm leading-snug font-semibold text-ink">
            {item.name}
          </span>
          <span className="text-xs text-ink-soft">
            {categoryLabel}
            {item.fabric ? ` · ${item.fabric}` : ''}
          </span>

          {/* Screen readers get the colour here — on a photo tile the swatch text is
              absent, and on a swatch tile it is aria-hidden decoration. */}
          <span className="sr-only">{colorText}</span>

          <span className="mt-0.5 flex flex-wrap items-center gap-1">
            {item.pattern !== 'solid' && <Chip>{item.pattern}</Chip>}
            {verdict && <Chip tone={verdict.tone}>{verdict.label}</Chip>}
            {!showPhoto && <Chip>no photo</Chip>}
          </span>
        </span>
      </button>
    </li>
  );
}
